/**
 * staffExamPurchaseStore — Staff Exam PIN purchases slice (Zustand).
 *
 *   GET  /api/staff/exam-purchases                      — list
 *   GET  /api/staff/exam-purchases/:id                  — detail (timeline + audit)
 *   GET  /api/staff/exam-purchases/stats                — purchase/fulfillment stats
 *   POST /api/staff/exam-purchases/:id/retry-fulfillment — retry fulfillment
 */
import { apiRequest, normalizeList } from "./staffUtils";
import { toDisplayString } from "../../utils/toDisplayString";

export const staffExamPurchaseInitialState = {
  staffExamPurchases: [],
  staffExamPurchasesLoading: false,
  staffExamPurchasesError: null,
  staffExamPurchasesTotal: 0,
  staffExamPurchasesPages: 1,

  staffExamPurchaseDetail: null,
  staffExamPurchaseDetailLoading: false,
  staffExamPurchaseDetailError: null,

  staffExamPurchaseStats: null,
  staffExamPurchaseStatsLoading: false,
  staffExamPurchaseStatsError: null,

  staffExamPurchaseMutationLoading: false,
  staffExamPurchaseMutationError: null,
};

export function createStaffExamPurchaseActions(set, get) {
  return {
    // GET /api/staff/exam-purchases
    fetchStaffExamPurchases: async (params = {}) => {
      set({ staffExamPurchasesLoading: true, staffExamPurchasesError: null });
      try {
        const query = new URLSearchParams(params).toString();
        const data = await apiRequest(
          get,
          `/api/staff/exam-purchases${query ? `?${query}` : ""}`
        );
        const { items, total, totalPages } = normalizeList(data, [
          "purchases",
          "examPurchases",
        ]);
        set({
          staffExamPurchasesLoading: false,
          staffExamPurchases: items,
          staffExamPurchasesTotal: total,
          staffExamPurchasesPages: totalPages,
        });
        return { success: true, items, total, totalPages };
      } catch (err) {
        const msg = toDisplayString(err.message, "Failed to fetch purchases");
        set({ staffExamPurchasesLoading: false, staffExamPurchasesError: msg });
        return { success: false, error: msg };
      }
    },

    // GET /api/staff/exam-purchases/:id
    fetchStaffExamPurchase: async (id) => {
      set({ staffExamPurchaseDetailLoading: true, staffExamPurchaseDetailError: null });
      try {
        const data = await apiRequest(get, `/api/staff/exam-purchases/${id}`);
        const detail = data?.data ?? data?.purchase ?? data;
        set({ staffExamPurchaseDetailLoading: false, staffExamPurchaseDetail: detail });
        return { success: true, data: detail };
      } catch (err) {
        const msg = toDisplayString(err.message, "Failed to fetch purchase");
        set({ staffExamPurchaseDetailLoading: false, staffExamPurchaseDetailError: msg });
        return { success: false, error: msg };
      }
    },

    // GET /api/staff/exam-purchases/stats
    fetchStaffExamPurchaseStats: async (params = {}) => {
      set({ staffExamPurchaseStatsLoading: true, staffExamPurchaseStatsError: null });
      try {
        const query = new URLSearchParams(params).toString();
        const data = await apiRequest(
          get,
          `/api/staff/exam-purchases/stats${query ? `?${query}` : ""}`
        );
        const stats = data?.data ?? data?.stats ?? data;
        set({ staffExamPurchaseStatsLoading: false, staffExamPurchaseStats: stats });
        return { success: true, stats };
      } catch (err) {
        const msg = toDisplayString(err.message, "Failed to fetch statistics");
        set({ staffExamPurchaseStatsLoading: false, staffExamPurchaseStatsError: msg });
        return { success: false, error: msg };
      }
    },

    // POST /api/staff/exam-purchases/:id/retry-fulfillment
    retryStaffExamPurchaseFulfillment: async (id) => {
      set({ staffExamPurchaseMutationLoading: true, staffExamPurchaseMutationError: null });
      try {
        // POST /api/staff/exam-purchases/:id/retry-fulfillment takes no body.
        await apiRequest(get, `/api/staff/exam-purchases/${id}/retry-fulfillment`, {
          method: "POST",
        });
        set({
          staffExamPurchaseMutationLoading: false,
          staffExamPurchaseMutationError: null,
          successMessage: "Fulfillment queued for retry",
        });
        return { success: true };
      } catch (err) {
        const msg = toDisplayString(err.message, "Failed to retry fulfillment");
        set({ staffExamPurchaseMutationLoading: false, staffExamPurchaseMutationError: msg });
        return { success: false, error: msg };
      }
    },
  };
}

const staffExamPurchaseActions = createStaffExamPurchaseActions;
export default staffExamPurchaseActions;
